import { ReactNode } from 'react';
import { useAtom } from 'jotai';
import { ButtonGroup, Tooltip } from '@mui/material';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import PhotoSizeSelectSmallIcon from '@mui/icons-material/PhotoSizeSelectSmall';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { TransformMode } from '@abyss/3d-viewer';
import { TRANSPARENT_COLOR } from '@/constants';
import { viewerBackground } from '@/theme/colors';
import { OutlinedIconButton } from './styles';
import { CuboidBase, CuboidState } from '../../types';

const TransformModeButton = ({
  title,
  transformModeToSelect,
  transformMode,
  setTransformMode,
  icon,
}: {
  title: string;
  transformModeToSelect: TransformMode;
  transformMode: TransformMode;
  setTransformMode: (mode: TransformMode) => void;
  icon: ReactNode;
}) => {
  const selected = transformMode === transformModeToSelect;
  return (
    <Tooltip title={title}>
      <OutlinedIconButton
        sx={{ background: selected ? '#fff' : TRANSPARENT_COLOR }}
        onClick={() => setTransformMode(transformModeToSelect)}
      >
        {icon}
      </OutlinedIconButton>
    </Tooltip>
  );
};

type CuboidTransformButtonGroupProps<CuboidT extends CuboidBase> = {
  cuboidState: CuboidState<CuboidT>;
};

export const CuboidTransformButtonGroup = <CuboidT extends CuboidBase>({
  cuboidState,
}: CuboidTransformButtonGroupProps<CuboidT>) => {
  const [transformMode, setTransformMode] = useAtom(cuboidState.cuboidTransformMode);

  return (
    <>
      <ButtonGroup sx={{ paddingBottom: '10px' }}>
        <TransformModeButton
          title="View"
          transformModeToSelect={TransformMode.View}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          icon={
            <RemoveRedEyeOutlinedIcon
              style={{
                color: transformMode === TransformMode.View ? viewerBackground : '#fff',
              }}
            />
          }
        />
        <TransformModeButton
          title="Resize"
          transformModeToSelect={TransformMode.BoxResize}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          icon={
            <PhotoSizeSelectSmallIcon
              style={{
                color: transformMode === TransformMode.BoxResize ? viewerBackground : '#fff',
              }}
            />
          }
        />
        <TransformModeButton
          title="Move"
          transformModeToSelect={TransformMode.Translate}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          icon={
            <OpenWithIcon
              style={{
                color: transformMode === TransformMode.Translate ? viewerBackground : '#fff',
              }}
            />
          }
        />
        <TransformModeButton
          title="Rotate"
          transformModeToSelect={TransformMode.Rotate}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          icon={
            <FlipCameraAndroidIcon
              style={{
                color: transformMode === TransformMode.Rotate ? viewerBackground : '#fff',
              }}
            />
          }
        />
        <TransformModeButton
          title="Symmetric Resize"
          transformModeToSelect={TransformMode.Scale}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          icon={
            <ViewInArIcon
              style={{
                color: transformMode === TransformMode.Scale ? viewerBackground : '#fff',
              }}
            />
          }
        />
      </ButtonGroup>
    </>
  );
};
